import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { CancelIcon, MenuIcon } from '@chegg-ui/theme';
import { sessionLogger, smoothScrollElement, useLayoutEffectIsomorphic } from '@chegg-ui/utils';
import { clickBaseEvent, signInRioLog } from '@chegg-me-components/utils';
import { MobileNavOverlay, MobileNavContent, MobileNavCloseButton, MobileNavScrollContainer, NavLinkText, StyledAccordion, NestedAccordion, TopLevelNavLink, HomeLink, MobileAccordionDivider, LinkAccordionPanel, SecondLevelNavLink, LinkAccordionHeader, ThirdLevelNavLink, UserInfoUL, UserInfoLI, UserProfileLink, SmallPaddedAvatar, UserTextContainer, UserProfileText, UserEmailText, UserInfoLink, Copyright, MenuFooterTerms, MenuFooter, MenuButton, mobileLinkHeightInPixels, VisuallyHidden } from '../../styles';
import UserInfoLoader from '../desktop/UserInfoLoader';
import useNavMenuBehavior from '../useNavMenuBehavior';
import { rioClient } from './../rio';
var MobileHamburgerMenu = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var navType = _ref.navType,
      activeSubLink = _ref.activeSubLink,
      envUrl = _ref.envUrl,
      pageTrackId = _ref.pageTrackId,
      user = _ref.user,
      onClickSignIn = _ref.onClickSignIn,
      navLinks = _ref.navLinks,
      userLinks = _ref.userLinks,
      userDataLoading = _ref.userDataLoading;

  var _useNavMenuBehavior = useNavMenuBehavior(),
      navMenuRef = _useNavMenuBehavior.navMenuRef,
      navHamburgerButtonRef = _useNavMenuBehavior.navHamburgerButtonRef,
      navMenuOpen = _useNavMenuBehavior.navMenuOpen,
      setNavMenuOpen = _useNavMenuBehavior.setNavMenuOpen,
      onNavKeyDown = _useNavMenuBehavior.onNavKeyDown,
      onNavCloseButtonClick = _useNavMenuBehavior.onNavCloseButtonClick;
  /* istanbul ignore next */


  useImperativeHandle(ref, function () {
    return navMenuRef.current;
  });
  var topLevelLinksRef = useRef(navLinks.map(function () {
    return /*#__PURE__*/React.createRef();
  }));
  var scrollContainerRef = useRef(null);
  var userUlRef = useRef(null);

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      activeAccordionIndex = _useState2[0],
      setActiveAccordionIndex = _useState2[1];

  var _useState3 = useState(-1),
      _useState4 = _slicedToArray(_useState3, 2),
      activeNestedAccordionIndex = _useState4[0],
      setActiveNestedAccordionIndex = _useState4[1];

  useEffect(function () {
    if (navMenuOpen) {
      var activeLinkIndexForType = navLinks.findIndex(function (linkObj) {
        return linkObj.type === navType;
      });
      var childLinks = activeLinkIndexForType >= 0 ? navLinks[activeLinkIndexForType].childLinks : null;
      var indexOfActiveSubLink = !childLinks ? -1 : childLinks.findIndex(function (linkObj) {
        return linkObj.href === activeSubLink;
      });
      setActiveAccordionIndex(activeLinkIndexForType);
      setActiveNestedAccordionIndex(indexOfActiveSubLink);
    }
  }, [navMenuOpen]); // we're just scrolling the active link to the top of the scroll container after opening, thus safe for SSR

  useLayoutEffectIsomorphic(function () {
    if (navMenuOpen && activeAccordionIndex >= 0) {
      var _topLevelLinksRef$cur;

      /* istanbul ignore next */
      var focusTarget =
      /* istanbul ignore next */
      (_topLevelLinksRef$cur = topLevelLinksRef.current[activeAccordionIndex]) === null || _topLevelLinksRef$cur === void 0 ? void 0 : _topLevelLinksRef$cur.current;
      /* istanbul ignore else */

      if (focusTarget) focusTarget.focus();
      var scrollTop = activeAccordionIndex * mobileLinkHeightInPixels;
      /* istanbul ignore else */

      if (scrollContainerRef.current) {
        smoothScrollElement(scrollContainerRef.current, {
          top: scrollTop
        });
      }

      document.body.style.overflow = 'hidden';
    }

    return function () {
      document.body.style.overflow = 'auto';
    };
  }, [navMenuOpen, activeAccordionIndex]); // TODO: this needs more work for https://chegg.atlassian.net/browse/CUI-631
  // // this whole hook is just used to replicate this logic:
  // // https://bitbucket.cheggnet.com/projects/OC/repos/chegg-header/browse/src/js/header/c.global.cheggheader.menu.js?at=refs%2Ftags%2FRELEASE_20200209_CHEGG-HEADER#281
  // useEffect(() => {
  //   /* istanbul ignore else */ if (
  //     navLinks.find(
  //       (link) =>
  //         link.childLinks &&
  //         link.childLinks.find((childLink) => childLink.href === 'study-pack')
  //     )
  //   ) {
  //     sessionLogger.event(envUrl, pageTrackId, { cs_mobile_nav: 'shown' });
  //   }
  // }, []);

  /**
   * Rio log for nested accordion
   * @param accordionCallIndex -> accordion index returned in callback (-1 means collapsed)
   * @param parentIndex -> index of main menu in hamburger
   */

  var nestedAccordionRioLog = function nestedAccordionRioLog(parentIndex, accordionCallIndex) {
    var _navLinks$parentIndex, _navLinks$parentIndex2;

    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
      data: {
        interaction: {
          type: !accordionCallIndex ? 'expand' : 'collapse',
          element: {
            name: "".concat((_navLinks$parentIndex = navLinks[parentIndex]) === null || _navLinks$parentIndex === void 0 ? void 0 : _navLinks$parentIndex.text, " hamburger menu item"),
            type: 'drop down',
            region_name: "".concat((_navLinks$parentIndex2 = navLinks[parentIndex]) === null || _navLinks$parentIndex2 === void 0 ? void 0 : _navLinks$parentIndex2.text, " hamburger menu")
          }
        }
      }
    }));
  };
  /**
   * Rio log for accordion click
   * @param index index returned by accordion callback and returned value is i+1 (-1,0 means collapsed)
   * @param collapsedIndex index which was opened before collapse
   */


  var accordionRioLog = function accordionRioLog(index, collapsedIndex) {
    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
      data: {
        interaction: {
          type: index > 0 ? 'expand' : 'collapse',
          element: {
            name: 'nav hamburger item',
            type: 'icon',
            region_name: 'nav hamburger',
            value: index > 0 ? navLinks[index].text : navLinks[collapsedIndex].text
          }
        }
      }
    }));
  };
  /**
   * Rio logs for logging main menu
   * links on opening the hamburger overlay
   * @param text
   */


  var rioLogMenuItem = function rioLogMenuItem(text) {
    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
      data: {
        interaction: {
          type: 'select',
          element: {
            name: 'nav hamburger item',
            type: 'link',
            region_name: 'nav hamburger',
            value: text
          }
        }
      }
    }));
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuButton, {
    onClick: function onClick() {
      /**
       * Rio logging on opening the menu dropdown
       */
      rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
        data: {
          interaction: {
            type: 'expand',
            element: {
              name: 'nav hamburger',
              type: 'drop down',
              region_name: 'nav hamburger'
            }
          }
        }
      })); // button is covered by the menu when it's open, so else case isn't possible

      /* istanbul ignore else */

      if (!navMenuOpen) {
        sessionLogger.event(envUrl, pageTrackId, {
          hamburger_open: true
        });
      }

      setNavMenuOpen(true);
    },
    screenReaderOnlyText: "navigation",
    "aria-expanded": navMenuOpen,
    ref: navHamburgerButtonRef,
    "data-test": "header-hamburger"
  }, /*#__PURE__*/React.createElement(MenuIcon, null)), /*#__PURE__*/React.createElement(MobileNavOverlay, {
    isOpen: navMenuOpen
  }), /*#__PURE__*/React.createElement(MobileNavContent, {
    ref: navMenuRef,
    isOpen: navMenuOpen,
    onKeyDown: onNavKeyDown,
    role: "navigation"
  }, /*#__PURE__*/React.createElement(MobileNavScrollContainer, {
    ref: scrollContainerRef,
    isOpen: navMenuOpen
  }, /*#__PURE__*/React.createElement(MobileNavCloseButton, {
    screenReaderOnlyText: "close navigation",
    onClick: onNavCloseButtonClick,
    "data-test": "header-close-hamburger"
  }, /*#__PURE__*/React.createElement(CancelIcon, null)), function () {
    if (navLinks && navLinks.length) {
      // the home link doesn't have any child links, so it can't be rendered in the accordion
      var homeLink = navLinks[0];
      var HomeIcon = homeLink.icon;
      return /*#__PURE__*/React.createElement(HomeLink, {
        href: "".concat(envUrl).concat(homeLink.href),
        "data-test": "header-".concat(homeLink.loggingKey),
        isActive: activeAccordionIndex === 0,
        onClick: function onClick() {
          sessionLogger.event(envUrl, pageTrackId, {
            hamburger_click: homeLink.loggingKey
          });
        },
        ref: topLevelLinksRef.current[0]
      }, /*#__PURE__*/React.createElement(HomeIcon, {
        size: "small",
        role: "presentation"
      }), !activeSubLink && navType === homeLink.type && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page: "), /*#__PURE__*/React.createElement(NavLinkText, null, homeLink.text));
    }
  }(), /*#__PURE__*/React.createElement(StyledAccordion // we need the funky addition/subtraction logic here to account for the fact that we can also
  // present the preceding home link with "active" status as well
  , {
    id: "mobile-hamburger-accordion",
    activeTabIndex: activeAccordionIndex - 1,
    onChangeIndex: function onChangeIndex(i) {
      setActiveAccordionIndex(i < 0 ? i : i + 1);
      accordionRioLog(i < 0 ? i : i + 1, activeAccordionIndex);
    },
    tabs: navLinks.slice(1).map(function (_ref2, linkIndex) {
      var text = _ref2.text,
          href = _ref2.href,
          type = _ref2.type,
          loggingKey = _ref2.loggingKey,
          childLinks = _ref2.childLinks,
          LinkIcon = _ref2.icon;
      // we know this is defined in all the remaining links
      var childrenLinks = childLinks;
      var isOnRoute = navType === type;
      var isActive = activeAccordionIndex - 1 === linkIndex;
      return {
        header: /*#__PURE__*/React.createElement(LinkAccordionHeader, null, /*#__PURE__*/React.createElement(TopLevelNavLink, {
          href: "".concat(envUrl).concat(href),
          "data-test": "header-".concat(loggingKey),
          onClick: function onClick(e) {
            e.stopPropagation();
            sessionLogger.event(envUrl, pageTrackId, {
              hamburger_click: loggingKey
            });
            rioLogMenuItem(text);
          } // we need to add one due to the Home link
          ,
          ref: topLevelLinksRef.current[linkIndex + 1]
        }, /*#__PURE__*/React.createElement(LinkIcon, {
          size: "small",
          role: "presentation"
        }), !activeSubLink && isOnRoute && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page: "), /*#__PURE__*/React.createElement(NavLinkText, null, text)), /*#__PURE__*/React.createElement(MobileAccordionDivider, {
          inverted: isActive
        })),
        panel: /*#__PURE__*/React.createElement(LinkAccordionPanel, null, childrenLinks.map(function (_ref3) {
          var childHref = _ref3.href,
              childText = _ref3.text,
              childLoggingKey = _ref3.loggingKey,
              grandchildLinks = _ref3.childLinks;
          return !grandchildLinks ? /*#__PURE__*/React.createElement(SecondLevelNavLink, {
            key: childText,
            href: "".concat(envUrl).concat(childHref),
            "data-test": "header-".concat(childLoggingKey),
            isActive: isOnRoute && activeSubLink === childHref,
            onClick: function onClick() {
              /**
               * Rio logging for child
               * menu in main menu
               */
              rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
                data: {
                  interaction: {
                    type: 'select',
                    element: {
                      name: 'nav hamburger menu item',
                      type: 'link',
                      region_name: text,
                      value: childText
                    }
                  }
                }
              }));
              sessionLogger.event(envUrl, pageTrackId, {
                hamburger_click: childLoggingKey
              }); // TODO: this needs more work for https://chegg.atlassian.net/browse/CUI-631
              // // this is here to recreate this logic
              // // https://bitbucket.cheggnet.com/projects/OC/repos/chegg-header/browse/src/js/header/c.global.cheggheader.menu.js?at=RELEASE_20200209_CHEGG-HEADER#175-185
              // if (childHref === 'study-pack') {
              //   sessionLogger.event(
              //     envUrl,
              //     pageTrackId,
              //     { cs_mobile_nav: 'clicked' }
              //   );
              //   if (
              //     typeof _satellite !==
              //       'undefined' &&
              //     typeof digitalData !==
              //       'undefined'
              //   ) {
              //     digitalData.event = [
              //       {
              //         eventName:
              //           'CS Mobile Hamburger Menu navigation',
              //         eventValue: {
              //           elName:
              //             'CS Mobile Hamburger Nav - Study Pack Selected',
              //           elRegion:
              //             'CS Mobile Hamburger Dedicated Nav',
              //         },
              //         eventID: 'event144',
              //         type: 'link',
              //         timeStamp: new Date(),
              //       },
              //     ];
              //     _satellite.track('event');
              //   }
              // }
            }
          }, activeSubLink === childHref && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page:", ' '), /*#__PURE__*/React.createElement(NavLinkText, null, childText)) : /*#__PURE__*/React.createElement(NestedAccordion, {
            key: childText,
            id: "mobile-nav-".concat(childText, "-links-accordion"),
            activeTabIndex: activeNestedAccordionIndex,
            onChangeIndex: function onChangeIndex(i) {
              setActiveNestedAccordionIndex(i);
              nestedAccordionRioLog(activeAccordionIndex, i);
            },
            tabs: [{
              header: /*#__PURE__*/React.createElement(LinkAccordionHeader, null, /*#__PURE__*/React.createElement(SecondLevelNavLink, {
                href: "".concat(envUrl).concat(childHref),
                isActive: isOnRoute && activeSubLink === childHref,
                "data-test": "header-".concat(childLoggingKey),
                onClick: function onClick(e) {
                  e.stopPropagation();
                  /**
                   * Rio logging for child
                   * menu in main menu
                   */

                  rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
                    data: {
                      interaction: {
                        type: 'select',
                        element: {
                          name: 'nav hamburger menu item',
                          type: 'link',
                          region_name: text,
                          value: childText
                        }
                      }
                    }
                  }));
                  sessionLogger.event(envUrl, pageTrackId, {
                    hamburger_click: childLoggingKey
                  });
                }
              }, activeSubLink === childHref && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page:", ' '), /*#__PURE__*/React.createElement(NavLinkText, null, childText)), /*#__PURE__*/React.createElement(MobileAccordionDivider, null)),
              panel: /*#__PURE__*/React.createElement(LinkAccordionPanel, null, grandchildLinks.map(function (_ref4) {
                var grandChildHref = _ref4.href,
                    grandChildText = _ref4.text,
                    grandChildLoggingKey = _ref4.loggingKey;
                return /*#__PURE__*/React.createElement(ThirdLevelNavLink, {
                  key: grandChildText,
                  "data-test": "header-".concat(grandChildLoggingKey),
                  href: "".concat(envUrl).concat(grandChildHref),
                  onClick: function onClick() {
                    /**
                     * Rio logging for grand child
                     * menu in child menu
                     */
                    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
                      data: {
                        interaction: {
                          type: 'select',
                          element: {
                            name: "".concat(text, " hamburger menu item"),
                            type: 'link',
                            region_name: "".concat(text, " hamburger menu"),
                            value: grandChildText
                          }
                        }
                      }
                    }));
                    sessionLogger.event(envUrl, pageTrackId, {
                      hamburger_click: grandChildLoggingKey
                    });
                  }
                }, activeSubLink === grandChildHref && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page:", ' '), /*#__PURE__*/React.createElement(NavLinkText, null, grandChildText));
              }))
            }]
          });
        }))
      };
    })
  }), userDataLoading ? /*#__PURE__*/React.createElement(UserInfoLoader, null) : /*#__PURE__*/React.createElement(UserInfoUL, {
    ref: userUlRef
  }, /*#__PURE__*/React.createElement(UserInfoLI, null, user && /*#__PURE__*/React.createElement(UserProfileLink, {
    href: "".concat(envUrl, "my/profile"),
    "data-test": "header-my-profile",
    onClick: function onClick() {
      sessionLogger.event(envUrl, pageTrackId, {
        usermenu_click: 'my-profile'
      });
      sessionLogger.event(envUrl, pageTrackId, {
        hamburger_click: 'my-profile'
      });
    }
  }, /*#__PURE__*/React.createElement(SmallPaddedAvatar, {
    size: 'medium',
    name: user.fname,
    image: {
      src: user.avatar,
      alt: ''
    }
  }), /*#__PURE__*/React.createElement(UserTextContainer, null, /*#__PURE__*/React.createElement(UserProfileText, null, user.fname, " ", user.lname), /*#__PURE__*/React.createElement(UserEmailText, null, user.email)))), userLinks.map(function (linkDetails) {
    return /*#__PURE__*/React.createElement(UserInfoLI, {
      key: linkDetails.text
    }, /*#__PURE__*/React.createElement(UserInfoLink, {
      href: "".concat(envUrl).concat(linkDetails.href),
      "data-test": "header-".concat(linkDetails.loggingKey),
      redirect: linkDetails.shouldRedirectBack,
      onClick: function onClick(e) {
        sessionLogger.event(envUrl, pageTrackId, {
          usermenu_click: linkDetails.loggingKey
        });
        sessionLogger.event(envUrl, pageTrackId, {
          hamburger_click: linkDetails.loggingKey
        });
        rioLogMenuItem(linkDetails.text);

        if (linkDetails.onClick) {
          onNavCloseButtonClick(e);
          linkDetails.onClick(e);
        }
      }
    }, /*#__PURE__*/React.createElement("span", null, linkDetails.text)));
  }), !user && /*#__PURE__*/React.createElement(UserInfoLI, null, /*#__PURE__*/React.createElement(UserInfoLink, {
    href: "".concat(envUrl, "auth"),
    redirect: true,
    "data-test": "header-signin",
    onClick: function onClick(e) {
      /**
       * Sign in Rio Log
       */
      signInRioLog();
      sessionLogger.event(envUrl, pageTrackId, {
        usermenu_click: 'signin'
      });
      sessionLogger.event(envUrl, pageTrackId, {
        hamburger_click: 'signin'
      });

      if (onClickSignIn) {
        onNavCloseButtonClick(e);
        onClickSignIn(e);
      }
    }
  }, "Sign in"))), /*#__PURE__*/React.createElement(MenuFooter, null, /*#__PURE__*/React.createElement(Copyright, null, "\xA9 2003-", new Date().getFullYear(), " Chegg Inc. All rights reserved."), /*#__PURE__*/React.createElement(MenuFooterTerms, null, /*#__PURE__*/React.createElement("a", {
    href: "".concat(envUrl, "termsofuse"),
    "data-test": "header-tou",
    onClick: function onClick() {
      sessionLogger.event(envUrl, pageTrackId, {
        hamburger_click: 'tou'
      });
    }
  }, "Privacy & Terms"))))));
});
MobileHamburgerMenu.displayName = 'MobileHamburgerMenu';
export default MobileHamburgerMenu;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg-me-components/header/dist/esm/shared/mobile/MobileHamburgerMenu.js
// module id = 1640
// module chunks = 11